import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { useState, ChangeEvent, FormEvent } from 'react';
import { config } from '../config/config';
import { useForm, ValidationError } from '@formspree/react';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface Errors {
  name?: string;
  email?: string;
  message?: string;
}

const ContactSection = () => {
  const [formData, setFormData] = useState<FormData>({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState<Errors>({});
  const [state, handleSubmit] = useForm(config.formSpreeId);
  const [isSent, setIsSent] = useState(false); // Nuevo estado para controlar si el formulario fue enviado

 

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    if (!formData.name) newErrors.name = 'Este campo es obligatorio';
    if (!formData.email) newErrors.email = 'Este campo es obligatorio';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.message) newErrors.message = 'Este campo es obligatorio';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      handleSubmit(e);
      setIsSent(true); // Cambia el estado para mostrar el mensaje de éxito
    }
  };

  return (
    <section className="bg-section-bg py-24 px-4 relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl"  />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative grid md:grid-cols-2 gap-12">
        <div className="space-y-8">
          <h2 className="text-5xl font-poppins font-bold text-primary-dark mb-4">
            Contáctanos
            <span className="ml-2 text-accent" >
              👋
            </span>
          </h2>

          <p className="text-lg text-gray-600 font-montserrat max-w-md leading-relaxed">
            ¿Tienes alguna pregunta o sugerencia? Escríbenos y te responderemos a la brevedad.
          </p>

          <div className="space-y-8">
            {[['Dirección', 'Av. Principal 1234, Ciudad', MapPin], ['Teléfono', '+1 234 567 890', Phone], ['Email', 'contacto@ejemplo.com', Mail]].map(([title, text, Icon]) => (
              <div className="flex items-start gap-4 group" key={title}>
                <div className="bg-primary p-3 rounded-full transition-all duration-300 group-hover:bg-dark" >
                  <Icon className="text-white text-2xl" />
                </div>
                <div>
                  <h3 className="text-xl font-poppins font-semibold text-primary-dark">{title}</h3>
                  <p className="text-gray-600 font-montserrat">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-main-bg p-8 rounded-2xl shadow-xl relative">
          <div className="absolute -top-6 -right-6">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center" >
              <Send className="text-white" />
            </div>
          </div>

          {isSent ? (
            <div 
              className="text-center p-8 bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl shadow-lg"
           
            >
              <div
                className="inline-block mb-4"
                
              >
                <CheckCircle className="text-green-600" size={64} />
              </div>
              <h3 className="text-2xl font-poppins font-bold text-green-800 mb-2">¡Mensaje enviado!</h3>
              <p className="text-gray-600 font-montserrat mb-6">Gracias por contactarnos. Te responderemos en breve.</p>
              <button
           
                className="bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-accent transition-colors duration-300"
                onClick={() => {
                  setFormData({ name: '', email: '', message: '' });
                  setIsSent(false); // Restablecer el estado para mostrar el formulario nuevamente
                }}
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit}>
              {(['name', 'email', 'message'] as (keyof Errors)[]).map((field, index) => (
                <div key={field} >
                  <label className="block text-primary-dark font-poppins mb-2">{field === 'name' ? 'Nombre' : field === 'email' ? 'Email' : 'Mensaje'}</label>
                  {field === 'message' ? (
                    <textarea
                      id={field}
                      name={field}
                      rows={5}
                      className="w-full p-3 border-2 border-primary-dark/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                      placeholder="Escribe tu mensaje..."
                      value={formData[field]}
                      onChange={handleInputChange}
                    />
                  ) : (
                    <input
                      id={field}
                      name={field}
                      type={field === 'email' ? 'email' : 'text'}
                      className="w-full p-3 border-2 border-primary-dark/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                      placeholder={`Tu ${field}`}
                      value={formData[field]}
                      onChange={handleInputChange}
                    />
                  )}
                  <ValidationError prefix={field} field={field} errors={state.errors} />
                </div>
              ))}

              <button type='button'  className="w-full bg-primary text-white py-4 px-6 rounded-xl font-poppins font-semibold hover:bg-accent flex items-center justify-center gap-2 transition-colors duration-300">
                Enviar Mensaje
                <span >✉️</span>
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
